/**
 * Login Page
 *
 * Authentication page with employee ID and password form.
 * Uses Ant Design form components with proper loading and error states.
 */
import { useState } from 'react';
import { Form, Input, Button, Card, Typography, Space, Alert } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService } from '../services/auth.service';
import { useAuthStore } from '../stores/authStore';
import { getDefaultRouteForRole } from '../../../shared/config/role-routes';
import { type EmployeeRole, EMPLOYEE_ROLES } from '../../../shared/types/employee.types';
import type { LoginRequest } from '../types/auth.types';

/** Set of valid EmployeeRole values for O(1) runtime validation */
const VALID_ROLES = new Set<string>(EMPLOYEE_ROLES);

const { Title, Text } = Typography;

/**
 * Login Page Component
 */
export function LoginPage(): JSX.Element {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [form] = Form.useForm<LoginRequest>();

  const handleSubmit = async (values: LoginRequest): Promise<void> => {
    setIsLoading(true);
    setErrorMessage(null);

    try {
      const response = await authService.login(values);

      if (response.success && response.data) {
        // Validate role before setting auth to prevent issues with invalid server responses
        const role = response.data.user.role;
        if (VALID_ROLES.has(role)) {
          setAuth(response.data.user, response.data.accessToken);
          const defaultRoute = getDefaultRouteForRole(role as EmployeeRole);
          navigate(defaultRoute, { replace: true });
        } else {
          // Invalid role - treat as authentication failure without setting auth state
          setErrorMessage(t('auth.loginFailed'));
        }
      } else {
        setErrorMessage(response.error?.message || t('auth.loginFailed'));
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : t('auth.loginNetworkError');
      setErrorMessage(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: '#f0f2f5',
      }}
    >
      <Card
        style={{
          width: 400,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <Title level={3} style={{ marginBottom: 8 }}>
              {t('auth.title')}
            </Title>
            <Text type="secondary">{t('auth.loginPrompt')}</Text>
          </div>

          {errorMessage && (
            <Alert
              message={errorMessage}
              type="error"
              showIcon
              closable
              onClose={() => setErrorMessage(null)}
            />
          )}

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            autoComplete="off"
            requiredMark={false}
          >
            <Form.Item
              name="employeeId"
              label={t('auth.employeeId')}
              rules={[
                { required: true, message: t('validation.required') as string },
              ]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder={t('auth.employeeIdPlaceholder') as string}
                size="large"
                autoComplete="username"
              />
            </Form.Item>

            <Form.Item
              name="password"
              label={t('auth.password')}
              rules={[
                { required: true, message: t('validation.required') as string },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder={t('auth.passwordPlaceholder') as string}
                size="large"
                autoComplete="current-password"
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                loading={isLoading}
              >
                {t('auth.loginButton')}
              </Button>
            </Form.Item>
          </Form>
        </Space>
      </Card>
    </div>
  );
}
